import { matchPath, generatePath } from 'react-router';
import { put, call, take, select, all, takeEvery } from 'redux-saga/effects';

import * as actionTypes from 'constants/ActionTypes';
import { PROJECTION } from 'constants/ApiConstants';
import { PATH } from 'constants/RoutesConstants';
import { STEP_DEFAULT_SMS, NEW_STEP_KEYS, FIXED_STEP_DEFAULT_MESSAGE } from 'constants/StepsConstants';
import { TranslationOrigin } from 'constants/translationConstants';

import { putAndWait } from 'modules/app/sagas';
import { scopeToPage } from 'modules/app/sagas/utils';
import { selectDealer } from 'modules/dealers/actions';
import { StepsCodeEnum, StepsTypeEnum } from 'modules/global/typing/enums/steps.enum';
import { fetchTranslations } from 'modules/i18n/actions';
import { navigate } from 'modules/router/navigate';
import { getUserGroups } from 'modules/userGroups/actions';
import { FETCH_USER_GROUPS_FAILURE, FETCH_USER_GROUPS_SUCCESS } from 'modules/userGroups/actionTypes';
import { getSelectedDealer } from 'selectors/dealerSelectors';
import { getLanguages } from 'selectors/languageSelectors';

import { getCurrentLocation, getLocationState } from 'selectors/router';

import { setStepDetailReady } from '../actions/details';
import { CREATE_STEP_SUCCESS } from '../actionTypes';

function* returnToDashboard(mode) {
  yield call(navigate, generatePath(PATH.STEPS_DASHBOARD.path, { mode }), { replace: true });
}

// eslint-disable-next-line consistent-return
function* initializeStepDetails() {
  yield put(setStepDetailReady(false));
  let dealer = yield select(getSelectedDealer);

  const location = yield select(getCurrentLocation);
  const locationState = yield select(getLocationState);
  const match = matchPath(PATH.STEPS_DETAILS.path, location);
  const { stepId, mode } = match.params;
  const dealerId = locationState?.dealerId;

  if (!dealerId) {
    return yield call(returnToDashboard, mode);
  }

  if (dealerId !== dealer?.id || !dealer?.config?.steps || dealer?.projection !== PROJECTION.CONFIG) {
    yield put(selectDealer(dealerId, { projection: PROJECTION.CONFIG }));
    const { type } = yield take([actionTypes.FETCH_DEALER_SUCCESS, actionTypes.FETCH_DEALER_FAILURE]);
    const haveFailed = type === actionTypes.FETCH_DEALER_FAILURE;
    if (haveFailed) {
      // if an error occurs while fetching the dealer
      return yield call(returnToDashboard, mode);
    }
    dealer = yield select(getSelectedDealer);
  }

  const step = dealer.config.steps[mode]?.steps?.find(({ id }) => id === stepId);
  if (!step && !Object.keys(NEW_STEP_KEYS).includes(stepId)) {
    // if the step provided in url doesn't exist in the dealer
    return yield call(returnToDashboard, mode);
  }
  const languages = yield select(getLanguages);
  const isFixed = step?.type === StepsTypeEnum.FIXED && step?.code;
  const isFollowUp = stepId === NEW_STEP_KEYS.newFollowUp || step?.code === StepsCodeEnum.FOLLOW_UP;

  if (isFixed || isFollowUp) {
    const code = isFollowUp ? StepsCodeEnum.FOLLOW_UP : step?.code;
    const keys = Object.values(FIXED_STEP_DEFAULT_MESSAGE[code] ?? {});
    if (keys.length > 0) {
      yield putAndWait(fetchTranslations({ languages, origin: TranslationOrigin.FO, keys, dealerId: dealer.id }));
    }
  }

  yield all([
    putAndWait(fetchTranslations({ languages, keys: [STEP_DEFAULT_SMS] })),
    put(getUserGroups(dealerId)),
    take([FETCH_USER_GROUPS_SUCCESS, FETCH_USER_GROUPS_FAILURE]),
  ]);
  yield put(setStepDetailReady(true));
}

/** @param {import("../types").CreateStepSuccessAction} action */
function* onCreationSuccess({ payload }) {
  const { step, dealerId, mode } = payload;
  const path = generatePath(PATH.STEPS_DETAILS.path, { mode, stepId: step.id });
  yield call(navigate, path, { replace: true, state: { dealerId } });
}

function* onDetailsWorker() {
  yield call(initializeStepDetails);
  yield takeEvery(CREATE_STEP_SUCCESS, onCreationSuccess);
}

export default function* stepsDetailsSaga() {
  yield scopeToPage([PATH.STEPS_DETAILS], onDetailsWorker);
}
